import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { VehicleIpvaLicensingService } from '../../../services/vehicle-ipva-licensing.service';
import { VehiclesService } from '../../../services/vehicles.service';
import {
  VehicleIpvaLicensingPaymentHistory,
  VehicleIpvaLicensingBankProvider,
  VehicleIpvaLicensingRequestPaymentPayload,
  VehicleIpvaLicensingRecordManualPayload
} from '../../../models/VehicleIpvaLicensing';
import { Vehicle } from '../../../models/Vehicle';
import { TableAction } from '../../../shared/components/table/table.component';

export interface PendencyRow extends VehicleIpvaLicensingPaymentHistory {
  vehiclePlate?: string;
  vehicleLabel?: string;
  _actions?: TableAction[];
}

@Component({
  selector: 'app-vehicle-ipva-licensing-pendencies',
  templateUrl: './vehicle-ipva-licensing-pendencies.component.html',
  styleUrls: ['./vehicle-ipva-licensing-pendencies.component.css']
})
export class VehicleIpvaLicensingPendenciesComponent implements OnInit {
  vehicles: Vehicle[] = [];
  vehicleMap: Map<number, Vehicle> = new Map();
  pendencies: PendencyRow[] = [];
  bankProvider: VehicleIpvaLicensingBankProvider | null = null;
  loading = false;
  error: string | null = null;

  filterVehicleId: number | null = null;
  filterType: '' | 'IPVA' | 'Licenciamento' = '';
  filterYear: number | null = null;

  showModalBank = false;
  showModalManual = false;
  submittingBank = false;
  submittingManual = false;
  bankSuccessMessage: string | null = null;
  boletoUrl: string | null = null;

  tableTitle = 'Pendências de IPVA e Licenciamento';
  showSearch = true;
  searchPlaceholder = 'Buscar por placa, tipo, documento...';
  clickable = true;

  columns = [
    { titulo: 'Veículo', label: 'vehicleLabel' },
    { titulo: 'Tipo', label: 'paymentType' },
    { titulo: 'Ano', label: 'referenceYear' },
    { titulo: 'Valor', label: 'amount' },
    { titulo: 'Data Pagamento', label: 'paymentDate' },
    { titulo: 'Status', label: 'status', badge: true },
    { titulo: 'Documento', label: 'documentNumber' }
  ];

  actions: TableAction[] = [
    { label: 'Ver', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Pagar via banco', icon: 'fas fa-university', action: 'payBank', class: 'btn-pay' },
    { label: 'Registrar manual', icon: 'fas fa-pen-to-square', action: 'recordManual', class: 'btn-edit' },
    { label: 'Atualizar status', icon: 'fas fa-sync', action: 'refresh', class: 'btn-edit' }
  ];

  formBank: Partial<VehicleIpvaLicensingRequestPaymentPayload> = {
    vehicleId: 0,
    paymentType: 'IPVA',
    referenceYear: new Date().getFullYear(),
    amount: 0,
    documentNumber: '',
    notes: ''
  };

  formManual: Partial<VehicleIpvaLicensingRecordManualPayload> = {
    vehicleId: 0,
    paymentType: 'IPVA',
    referenceYear: new Date().getFullYear(),
    amount: 0,
    paymentDate: new Date().toISOString().slice(0, 10),
    documentNumber: '',
    notes: ''
  };

  currentYears: number[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ipvaLicensingService: VehicleIpvaLicensingService,
    private vehiclesService: VehiclesService
  ) {
    const y = new Date().getFullYear();
    this.currentYears = [y, y - 1, y - 2, y - 3, y - 4];
  }

  ngOnInit(): void {
    const vId = this.route.snapshot.queryParamMap.get('vehicleId');
    if (vId) this.filterVehicleId = +vId;
    this.loadBankProvider();
    this.loadAllPendencies();
  }

  loadBankProvider(): void {
    this.ipvaLicensingService.getBankProvider().subscribe({
      next: p => (this.bankProvider = p),
      error: () => (this.bankProvider = { isAvailable: false, providerName: 'Nenhum' })
    });
  }

  loadAllPendencies(): void {
    this.loading = true;
    this.error = null;
    this.vehiclesService.getActive(1, 500).pipe(
      catchError(() => of([]))
    ).subscribe(vehicles => {
      this.vehicles = vehicles;
      this.vehicleMap = new Map(vehicles.map(v => [v.id, v]));
      if (vehicles.length === 0) {
        this.pendencies = [];
        this.loading = false;
        return;
      }
      const requests = vehicles.map(v => this.ipvaLicensingService.getByVehicle(v.id).pipe(catchError(() => of([]))));
      forkJoin(requests).pipe(
        map((arrays: VehicleIpvaLicensingPaymentHistory[][]) => {
          const merged: VehicleIpvaLicensingPaymentHistory[] = [];
          arrays.forEach(arr => merged.push(...arr));
          return merged.filter(h => (h.status || '').toLowerCase() === 'pendente');
        })
      ).subscribe({
        next: list => {
          this.pendencies = this.buildRows(list);
          this.loading = false;
        },
        error: err => {
          this.error = err?.message || 'Erro ao carregar pendências';
          this.loading = false;
          this.pendencies = [];
        }
      });
    });
  }

  private buildRows(list: VehicleIpvaLicensingPaymentHistory[]): PendencyRow[] {
    return list.map(item => {
      const vehicle = this.vehicleMap.get(item.vehicleId);
      const vehicleLabel = vehicle ? `${vehicle.plateNumber} — ${vehicle.brand} ${vehicle.model}` : `Veículo #${item.vehicleId}`;
      const actions: TableAction[] = [{ label: 'Ver', icon: 'fas fa-eye', action: 'view', class: 'btn-view' }];
      if (this.bankProvider?.isAvailable) {
        actions.push({ label: 'Pagar via banco', icon: 'fas fa-university', action: 'payBank', class: 'btn-pay' });
      }
      actions.push({ label: 'Registrar manual', icon: 'fas fa-pen-to-square', action: 'recordManual', class: 'btn-edit' });
      if (item.externalReference) {
        actions.push({ label: 'Atualizar status', icon: 'fas fa-sync', action: 'refresh', class: 'btn-edit' });
      }
      return {
        ...item,
        vehiclePlate: vehicle?.plateNumber,
        vehicleLabel,
        _actions: actions
      };
    });
  }

  getFilteredData(): PendencyRow[] {
    let list = [...this.pendencies];
    if (this.filterVehicleId != null && this.filterVehicleId > 0) {
      list = list.filter(p => p.vehicleId === this.filterVehicleId);
    }
    if (this.filterType === 'IPVA' || this.filterType === 'Licenciamento') {
      list = list.filter(p => p.paymentType === this.filterType);
    }
    if (this.filterYear != null && this.filterYear > 0) {
      list = list.filter(p => p.referenceYear === this.filterYear);
    }
    return list.sort((a, b) => b.referenceYear - a.referenceYear || (a.paymentType === 'IPVA' ? -1 : 1));
  }

  onFilterChange(): void {}

  clearFilters(): void {
    this.filterVehicleId = null;
    this.filterType = '';
    this.filterYear = null;
  }

  openModalBank(row?: PendencyRow): void {
    this.formBank = {
      vehicleId: row?.vehicleId ?? (this.vehicles[0]?.id ?? 0),
      paymentType: row?.paymentType ?? 'IPVA',
      referenceYear: row?.referenceYear ?? new Date().getFullYear(),
      amount: row?.amount ?? 0,
      documentNumber: row?.documentNumber ?? '',
      notes: ''
    };
    this.bankSuccessMessage = null;
    this.boletoUrl = null;
    this.showModalBank = true;
  }

  closeModalBank(): void {
    this.showModalBank = false;
    this.loadAllPendencies();
  }

  submitBank(): void {
    const vehicleId = Number(this.formBank.vehicleId) || this.vehicles[0]?.id;
    if (!vehicleId || this.submittingBank) return;
    const payload: VehicleIpvaLicensingRequestPaymentPayload = {
      vehicleId,
      paymentType: this.formBank.paymentType!,
      referenceYear: this.formBank.referenceYear!,
      amount: Number(this.formBank.amount) || 0,
      documentNumber: this.formBank.documentNumber || undefined,
      notes: this.formBank.notes || undefined
    };
    this.submittingBank = true;
    this.ipvaLicensingService.requestPayment(payload).subscribe({
      next: res => {
        this.submittingBank = false;
        this.bankSuccessMessage = res.message;
        this.boletoUrl = res.boletoUrl || null;
        if (res.history) this.loadAllPendencies();
      },
      error: () => (this.submittingBank = false)
    });
  }

  openModalManual(row?: PendencyRow): void {
    this.formManual = {
      vehicleId: row?.vehicleId ?? (this.vehicles[0]?.id ?? 0),
      paymentType: row?.paymentType ?? 'IPVA',
      referenceYear: row?.referenceYear ?? new Date().getFullYear(),
      amount: row?.amount ?? 0,
      paymentDate: new Date().toISOString().slice(0, 10),
      documentNumber: row?.documentNumber ?? '',
      notes: ''
    };
    this.showModalManual = true;
  }

  closeModalManual(): void {
    this.showModalManual = false;
    this.loadAllPendencies();
  }

  submitManual(): void {
    const vehicleId = Number(this.formManual.vehicleId) || this.vehicles[0]?.id;
    if (!vehicleId || this.submittingManual) return;
    const payload: VehicleIpvaLicensingRecordManualPayload = {
      vehicleId,
      paymentType: this.formManual.paymentType!,
      referenceYear: this.formManual.referenceYear!,
      amount: Number(this.formManual.amount) || 0,
      paymentDate: this.formManual.paymentDate!,
      documentNumber: this.formManual.documentNumber || undefined,
      notes: this.formManual.notes || undefined
    };
    this.submittingManual = true;
    this.ipvaLicensingService.recordManual(payload).subscribe({
      next: () => {
        this.submittingManual = false;
        this.closeModalManual();
      },
      error: () => (this.submittingManual = false)
    });
  }

  onActionClick(data: { action: string; item: any }): void {
    const row = data.item as PendencyRow;
    if (data.action === 'view') {
      this.router.navigate(['/financial/ipva-licensing/detalhe', row.id], { queryParams: { vehicleId: row.vehicleId } });
    } else if (data.action === 'payBank') {
      this.openModalBank(row);
    } else if (data.action === 'recordManual') {
      this.openModalManual(row);
    } else if (data.action === 'refresh' && row.externalReference) {
      this.ipvaLicensingService.refreshStatus(row.id).subscribe({
        next: res => { if (res.updated) this.loadAllPendencies(); }
      });
    }
  }

  retryLoad(): void {
    this.loadAllPendencies();
  }
}
